export const FOOD_IMAGES: Record<string, string> = {
  apple: new URL('../assets/apple.png', import.meta.url).href,
  burger: new URL('../assets/burger.png', import.meta.url).href,
  coffee: new URL('../assets/coffee.png', import.meta.url).href,
  'healthy-food': new URL('../assets/healthy-food.png', import.meta.url).href,
  pizza: new URL('../assets/pizza.png', import.meta.url).href,
  ramen: new URL('../assets/ramen.png', import.meta.url).href,
  sushi: new URL('../assets/sushi.png', import.meta.url).href,
};

export const UTILITY_IMAGES = {
  leaf: new URL('../assets/animal-crossing-leaf.png', import.meta.url).href,
  calendar: new URL('../assets/calendar.png', import.meta.url).href,
  microphone: new URL('../assets/karaoke-microphone.png', import.meta.url).href,
  map: new URL('../assets/map.png', import.meta.url).href,
  camera: new URL('../assets/polaroid-camera.png', import.meta.url).href,
  instructions: new URL('../assets/info-icon.png', import.meta.url).href,
};

export interface MealEntry {
  id: string;
  dayOfWeek: number; // 0: Sun, 1: Mon, 2: Tue...
  timestamp: string; // "08:15 AM"
  dateStr: string; // "2026-05-25"
  foodType: string;
  customName: string;
  photoUrl?: string; // base64 webcam photo URL
  audioUrl?: string; // base64 or blob URL of real recorded voice
  voiceDuration: number; // in seconds
  gps: {
    lat: number;
    lng: number;
    locationName: string;
    isCampus: boolean;
  };
  socialTag: string;
  locationType: string;
  decisionReasons: string[];
  emotions: {
    before: number; // 1-5
    during: number; // 1-5
    after: number; // 1-5
  };
  overallScore: number; // 1-10
}

const STORAGE_KEY = 'food_journey_meals';
const MILES_KEY = 'food_journey_miles';
const TOKEN_KEY = 'food_journey_token';

// Get current week's dates starting from Monday
export function getWeekDates(baseDate: Date = new Date()): Date[] {
  const currentDay = baseDate.getDay();
  const distance = currentDay === 0 ? -6 : 1 - currentDay;
  const monday = new Date(baseDate);
  monday.setDate(baseDate.getDate() + distance);
  monday.setHours(0, 0, 0, 0);

  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const nextDate = new Date(monday);
    nextDate.setDate(monday.getDate() + i);
    dates.push(nextDate);
  }
  return dates;
}

export function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Generate simple base64 silent audio to serve as placeholder for mock data
// It is a tiny, valid 1-second silent WAV base64
const SILENT_WAV = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAAA';

const DEFAULT_MEALS = (weekDates: Date[]): MealEntry[] => [
  {
    id: 'mock-1',
    dayOfWeek: 1, // Monday
    timestamp: '08:15 AM',
    dateStr: formatDateStr(weekDates[0]),
    foodType: 'apple',
    customName: 'An apple & black coffee',
    audioUrl: SILENT_WAV,
    voiceDuration: 6,
    gps: {
      lat: 42.3756,
      lng: -71.1156,
      locationName: 'Dormitory Building 3 lobby',
      isCampus: true,
    },
    socialTag: 'Alone',
    locationType: 'Dorm',
    decisionReasons: ['Convenience', 'Health', 'Habit'],
    emotions: {
      before: 3,
      during: 3,
      after: 4,
    },
    overallScore: 7,
  },
  {
    id: 'mock-2',
    dayOfWeek: 1, // Monday
    timestamp: '12:20 PM',
    dateStr: formatDateStr(weekDates[0]),
    foodType: 'ramen',
    customName: 'Hot Tonkotsu Ramen',
    audioUrl: SILENT_WAV,
    voiceDuration: 8,
    gps: {
      lat: 42.3712,
      lng: -71.1210,
      locationName: 'Main Campus Canteen, 1st Floor',
      isCampus: true,
    },
    socialTag: 'Classmates',
    locationType: 'Canteen',
    decisionReasons: ['Taste', 'Price', 'Convenience'],
    emotions: {
      before: 2,
      during: 4,
      after: 5,
    },
    overallScore: 9,
  },
];

export function getMeals(): MealEntry[] {
  const localData = localStorage.getItem(STORAGE_KEY);
  if (localData) {
    try {
      return JSON.parse(localData);
    } catch (e) {
      console.error('Error parsing meals', e);
    }
  }

  const weekDates = getWeekDates();
  const defaults = DEFAULT_MEALS(weekDates);
  saveMeals(defaults);
  return defaults;
}

export function saveMeals(meals: MealEntry[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(meals));
}

export function addMeal(meal: Omit<MealEntry, 'id'>): MealEntry {
  const meals = getMeals();
  const newMeal: MealEntry = {
    ...meal,
    id: 'meal-' + Date.now() + '-' + Math.floor(Math.random() * 1000),
  };
  meals.push(newMeal);
  saveMeals(meals);

  addMiles(150);
  return newMeal;
}

export function getMiles(): number {
  const miles = localStorage.getItem(MILES_KEY);
  if (miles === null) {
    localStorage.setItem(MILES_KEY, '500');
    return 500;
  }
  return parseInt(miles, 10);
}

export function addMiles(amount: number): number {
  const current = getMiles();
  const updated = current + amount;
  localStorage.setItem(MILES_KEY, String(updated));
  return updated;
}

// Token helper for kit instruction landing page check
export function getKitToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function saveKitToken(): void {
  localStorage.setItem(TOKEN_KEY, 'probe-token-' + Date.now());
}

export function getRealLatLng(lat: number, lng: number): [number, number] {
  // If longitude is positive, it's a flat percentage (0-100) from the old map
  if (lng > 0) {
    const realLat = 42.3736 + (lat - 50) * 0.0002;
    const realLng = -71.1186 + (lng - 50) * 0.0002;
    return [realLat, realLng];
  }
  return [lat, lng];
}
